import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Register from './pages/Register';
import Login from './pages/Login';
import Marketplace from './pages/Marketplace';
import DoctorProfile from './pages/DoctorProfile';
import DoctorDashboard from './pages/DoctorDashboard';
import DoctorPayments from './pages/DoctorPayments';
import DoctorServices from './pages/DoctorServices';
import DoctorAvailability from './pages/DoctorAvailability';
import DoctorSettings from './pages/DoctorSettings';
import DoctorProfileEdit from './pages/DoctorProfileEdit';
import DoctorLocations from './pages/DoctorLocations';
import DoctorCancellationPolicy from './pages/DoctorCancellationPolicy';
import DoctorEmbed from './pages/DoctorEmbed';
import DoctorLinkInBio from './pages/DoctorLinkInBio';
import ClinicDashboard from './pages/ClinicDashboard';
import ClinicLinkInBio from './pages/ClinicLinkInBio';
import ClinicProfile from './pages/ClinicProfile';
import PatientAppointments from './pages/PatientAppointments';
import PatientFavorites from './pages/PatientFavorites';
import AdminDashboard from './pages/AdminDashboard';
import AdminDoctors from './pages/AdminDoctors';
import AdminReviews from './pages/AdminReviews';
import AppointmentDetail from './pages/AppointmentDetail';
import TimeoutTest from './pages/TimeoutTest';
import ServerErrorTest from './pages/ServerErrorTest';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Pricing from './pages/Pricing';
import DoctorSubscription from './pages/DoctorSubscription';
import StripeOnboardingDev from './pages/StripeOnboardingDev';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import GoogleCallback from './pages/GoogleCallback';
import SlugResolver from './pages/SlugResolver';
import Footer from './components/Footer';

// Layout wrapper with Navbar and Footer
const Layout = ({ children }) => (
  <div className="min-h-screen bg-light-bg dark:bg-obsidian text-light-text dark:text-silver transition-colors duration-200 flex flex-col">
    <Navbar />
    <main id="main-content" tabIndex="-1" className="pt-20 outline-none flex-grow">
      {children}
    </main>
    <Footer />
  </div>
);

// Embed Layout - no navbar/footer, clean widget appearance
const EmbedLayout = ({ children }) => (
  <div className="min-h-screen bg-light-bg dark:bg-obsidian text-light-text dark:text-silver transition-colors duration-200">
    <main id="main-content" tabIndex="-1" className="outline-none">
      {children}
    </main>
  </div>
);

// Wrapper for DoctorProfile that detects embed mode
const DoctorProfileRoute = () => {
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';

  if (isEmbed) {
    return (
      <EmbedLayout>
        <DoctorProfile />
      </EmbedLayout>
    );
  }

  return (
    <Layout>
      <DoctorProfile />
    </Layout>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        // Include the intended destination as a return URL parameter
        const returnUrl = encodeURIComponent(location.pathname + location.search);
        navigate(`/login?returnUrl=${returnUrl}`, { replace: true });
      } else if (allowedRoles && !allowedRoles.includes(user?.role)) {
        navigate('/', { replace: true });
      }
    }
  }, [loading, isAuthenticated, user, allowedRoles, location.pathname, location.search, navigate]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pure-white"></div>
        </div>
      </Layout>
    );
  }

  if (!isAuthenticated || (allowedRoles && !allowedRoles.includes(user?.role))) {
    // Show loading while redirect is happening
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pure-white"></div>
        </div>
      </Layout>
    );
  }

  return children;
};

// Placeholder components - to be implemented
const Home = () => (
  <Layout>
    <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
      <div className="text-center">
        <h1 className="font-display text-6xl italic tracking-tighter leading-tight mb-4 text-light-text dark:text-pure-white">
          Calendiary
        </h1>
        <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
          Medical Appointment Booking Platform
        </p>
      </div>
    </div>
  </Layout>
);

const NotFound = () => (
  <Layout>
    <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
      <div className="text-center">
        <h1 className="font-display text-6xl italic mb-4 text-light-text dark:text-pure-white">404</h1>
        <p className="font-body text-lg text-light-text-secondary dark:text-silver/60 mb-6">Page not found</p>
        <p className="font-body text-light-text-secondary dark:text-silver/60 mb-8">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <a
          href="/"
          className="inline-flex items-center gap-2 px-6 py-3 bg-obsidian dark:bg-pure-white text-pure-white dark:text-obsidian font-body font-medium rounded-lg hover:opacity-90 transition-opacity"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
          </svg>
          Back to Homepage
        </a>
      </div>
    </div>
  </Layout>
);

const PatientDashboard = () => (
  <Layout>
    <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
      <div className="text-center">
        <h1 className="font-display text-4xl italic mb-4 text-light-text dark:text-pure-white">Patient Dashboard</h1>
        <p className="font-body text-light-text-secondary dark:text-silver/60">Coming soon</p>
      </div>
    </div>
  </Layout>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Home />} />
        <Route path="/register" element={<Layout><Register /></Layout>} />
        <Route path="/login" element={<Layout><Login /></Layout>} />
        <Route path="/marketplace" element={<Layout><Marketplace /></Layout>} />
        <Route path="/doctor/:id" element={<DoctorProfileRoute />} />
        <Route path="/test/timeout" element={<Layout><TimeoutTest /></Layout>} />
        <Route path="/test/server-error" element={<Layout><ServerErrorTest /></Layout>} />
        <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
        <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
        <Route path="/pricing" element={<Layout><Pricing /></Layout>} />
        <Route path="/terms-of-service" element={<Layout><TermsOfService /></Layout>} />
        <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />

        {/* Stripe Dev Mode routes (for testing without real Stripe Connect) */}
        {/* This route is public because it simulates the Stripe onboarding page */}
        <Route path="/doctor/stripe-onboarding-dev" element={<StripeOnboardingDev />} />

        {/* Doctor routes */}
        <Route
          path="/doctor/dashboard"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorDashboard /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/payments"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorPayments /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/services"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorServices /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/availability"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorAvailability /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/settings"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorSettings /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/profile"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorProfileEdit /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/appointments/:id"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><AppointmentDetail /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/subscription"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorSubscription /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/locations"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorLocations /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/cancellation-policy"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorCancellationPolicy /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/embed"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorEmbed /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor/link-in-bio"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <Layout><DoctorLinkInBio /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Patient routes */}
        <Route
          path="/patient/dashboard"
          element={
            <ProtectedRoute allowedRoles={['patient']}>
              <PatientDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/patient/appointments"
          element={
            <ProtectedRoute allowedRoles={['patient']}>
              <Layout><PatientAppointments /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/patient/appointments/:id"
          element={
            <ProtectedRoute allowedRoles={['patient']}>
              <Layout><AppointmentDetail /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/patient/favorites"
          element={
            <ProtectedRoute allowedRoles={['patient']}>
              <Layout><PatientFavorites /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Clinic routes */}
        <Route
          path="/clinic/dashboard"
          element={
            <ProtectedRoute allowedRoles={['clinic_owner']}>
              <Layout><ClinicDashboard /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clinic/link-in-bio"
          element={
            <ProtectedRoute allowedRoles={['clinic_owner']}>
              <Layout><ClinicLinkInBio /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clinic/:id"
          element={
            <Layout><ClinicProfile /></Layout>
          }
        />

        {/* Admin routes */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><AdminDashboard /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/doctors"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><AdminDoctors /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reviews"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><AdminReviews /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Auth callback */}
        <Route path="/auth/google/callback" element={<GoogleCallback />} />

        {/* Explicit 404 route - must be before slug resolver */}
        <Route path="/404" element={<NotFound />} />

        {/* Slug resolver - must be before catch-all but after all specific routes */}
        <Route path="/:slug" element={<SlugResolver />} />

        {/* Catch all - 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
